/*
  Web counterpart of `microlab/src/utils/googleSignIn.utils.ts`.

  Browsers can't link against `@react-native-google-signin/google-signin`
  (that ships native iOS / Android code), so the dashboard uses Google's
  official **Google Identity Services** library instead. The library
  emits the same kind of credential the mobile app gets — a Google-signed
  JWT (the "idToken") whose `sub` claim is a stable per-user identifier
  shared across web + mobile, *as long as both clients live in the same
  Google Cloud project*. They do (project `microlab-expo`), so a user
  who signs in with Google on the website ends up matching the same
  Microlab `user` row they have on their phone.

  Why we render Google's official button (instead of styling our own):

    1. **Google Brand Guidelines** require it: third parties may not
       restyle the Google logo or colors. Using `renderButton` is the
       only path that's always compliant.
    2. It's the only GIS API that reliably produces an idToken on a
       direct user click. The alternatives —
         • `google.accounts.id.prompt()`   → One-Tap, Chrome-gated, no
           guarantee it will appear.
         • `google.accounts.oauth2.*`      → emits an access token, NOT
           an idToken. We'd then have to call userinfo to recover
           `sub` + `email`, costing a second round-trip and breaking
           parity with the mobile flow.
       `renderButton` sidesteps both issues.

  Required env (set on the build host):

    REACT_APP_GOOGLE_CLIENT_ID
      OAuth 2.0 Web client id from Google Cloud Console for the
      `microlab-expo` project. Same value as `client_type: 3` in
      android/app/google-services.json so the resulting `sub` matches
      across web + Android. Defaults to the production web client id
      (hard-coded fallback below) so a fresh dev build still works
      without any env tweaking — but PRODUCTION builds should always
      set this explicitly so a leaked client id can be rotated without
      a code release.
*/

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

/*
  The "Web" OAuth 2.0 client id Microlab uses across iOS + Android +
  web — kept in sync with `GOOGLE_WEB_CLIENT_ID` in
  `microlab/src/utils/googleSignIn.utils.ts`. If either side rotates
  the value, BOTH must be updated; otherwise the `aud` claim on the
  emitted idToken won't match what the BE expects, and the resulting
  `sub` could differ across platforms (it doesn't usually, since `sub`
  is per-Google-project not per-client, but having mismatched `aud`
  values makes future server-side token verification harder).
*/
const DEFAULT_WEB_CLIENT_ID =
  "680710777724-8cggsu64ceibbfnlis8mt59f58n0g73s.apps.googleusercontent.com";

/**
 * Read the build-time config. `enabled` is true whenever a client id
 * is available — the production fallback above means this is `true`
 * by default, but the UI still treats it as a feature flag in case
 * an operator deliberately wipes the env var to disable Google.
 */
export function getGoogleWebSignInConfig() {
  /*
    Keep this hard-coded for the dashboard instead of reading `.env`.
    CRA only reads env vars at dev-server startup, which made local
    debugging confusing: changing `.env` looked like it "didn't work"
    until a full restart. The OAuth client itself still has to allow the
    current browser origin in Google Cloud Console, otherwise GIS returns
    `origin_mismatch` before our callback ever runs.
  */
  const clientId = DEFAULT_WEB_CLIENT_ID;
  return { clientId, enabled: Boolean(clientId) };
}

/**
 * Idempotent loader for the GIS bootstrap script. Resolves once
 * `window.google?.accounts?.id` is reachable. We check if the script
 * is already on the page (e.g. another component pre-loaded it) so
 * remounts don't double-inject.
 */
export function loadGoogleAuthScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Sign In requires a browser."));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
    const onLoad = () => {
      if (window.google?.accounts?.id) {
        resolve();
      } else {
        reject(new Error("Google Sign In script loaded but API is missing."));
      }
    };
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", () =>
        reject(new Error("Google Sign In script failed to load."))
      );
      return;
    }
    const s = document.createElement("script");
    s.src = GIS_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = onLoad;
    s.onerror = () =>
      reject(new Error("Google Sign In script failed to load."));
    document.head.appendChild(s);
  });
}

/*
  Decode the JWT body (the "ID Token" GIS hands us). The signature is
  NOT verified here — the BE is the trust boundary for that. We only
  need the `sub` (stable user id) and `email` claims to build the
  payload for our `auth/social_auth` endpoint. This mirrors what the
  mobile library hands the app: it runs on the device, decodes the
  same JWT, and the BE looks the user up by `social_auth_id` (= `sub`).

  ⚠ Future hardening note: the BE currently trusts a client-supplied
  `social_auth_id` for Google, the same way the mobile flow always has.
  If we ever tighten the BE to verify Google idTokens server-side
  (the way it already does for Apple via `apple_identity_token`), we
  should send the raw `idToken` here too — the helper already returns
  it for that future migration.
*/
function decodeGoogleJwtPayload(idToken) {
  try {
    const parts = String(idToken).split(".");
    if (parts.length < 2) return {};
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

/**
 * Render Google's official "Sign in with Google" button into `targetEl`
 * and subscribe `onCredential` to fire every time a user successfully
 * picks an account.
 *
 * Why not a plain "click" handler we control? GIS only fires the
 * credential callback for clicks that originate INSIDE the rendered
 * button — wrapping it in our own `<button onClick={...}>` won't work.
 * So we expose a render helper instead, and the caller drops a `<div>`
 * for us to populate.
 *
 * Why a subscription instead of a Promise? The button is reusable: a
 * user might click it, fail (e.g. network blip during BE round-trip),
 * and click again. A Promise-based API can only resolve once, so the
 * second click would silently no-op. Calling `onCredential` multiple
 * times across the page's lifetime mirrors how the mobile button works.
 *
 * `theme` and `text` let the caller match their layout (e.g. dark CTA
 * row vs. light card). `width` is the rendered pixel width — Google
 * caps this at 400px, anything wider is clamped.
 *
 * Returns an `unsubscribe` function the caller should invoke on unmount
 * so we don't leak the GIS callback after the React tree is torn down.
 */
export async function setupGoogleSignInButton({
  targetEl,
  onCredential,
  onError,
  theme = "outline",
  text = "signin_with",
  width = 320,
}) {
  if (!targetEl) {
    throw new Error("setupGoogleSignInButton: targetEl is required.");
  }
  if (typeof onCredential !== "function") {
    throw new Error("setupGoogleSignInButton: onCredential must be a function.");
  }
  const { clientId, enabled } = getGoogleWebSignInConfig();
  if (!enabled) {
    throw new Error("Google Sign In is not configured for this site.");
  }
  await loadGoogleAuthScript();

  let alive = true;

  /*
    `initialize` is global — calling it again on a different page
    overwrites the previously registered callback. That's actually
    what we want here: the most recent page wins. The unsubscribe
    handle below flips `alive` so a delayed callback from a stale
    page (e.g. user clicked, navigated, then the popup completed)
    quietly drops on the floor instead of dispatching into a
    teardown'd component.
  */
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      if (!alive) return;
      try {
        const idToken = response?.credential;
        if (!idToken) {
          // GIS only ever invokes the callback on success in the
          // popup flow; a missing credential here is a sign of an
          // unsupported response shape, not a user-cancel.
          onError?.(new Error("Google sign-in returned no credential."));
          return;
        }
        const payload = decodeGoogleJwtPayload(idToken);
        const sub = payload.sub;
        const email = payload.email;
        if (!sub || !email) {
          onError?.(
            new Error(
              "Google sign-in returned an unexpected shape (missing sub/email)."
            )
          );
          return;
        }
        onCredential({
          idToken,
          sub: String(sub),
          email: String(email),
          emailVerified: Boolean(payload.email_verified),
          givenName: payload.given_name || null,
          familyName: payload.family_name || null,
          name: payload.name || null,
          picture: payload.picture || null,
        });
      } catch (err) {
        onError?.(err);
      }
    },
    auto_select: false,
    ux_mode: "popup",
    context: "signin",
  });

  window.google.accounts.id.renderButton(targetEl, {
    theme,
    text,
    size: "large",
    shape: "rectangular",
    logo_alignment: "left",
    width,
  });

  return () => {
    alive = false;
  };
}

/**
 * Best-effort "sign out" that clears any cached Google credential so
 * the next button render forces a fresh account picker. Safe to call
 * even if the GIS script never loaded — same shape as the mobile
 * `signOutFromGoogle` helper.
 */
export function disableGoogleAutoSelect() {
  try {
    if (window?.google?.accounts?.id?.disableAutoSelect) {
      window.google.accounts.id.disableAutoSelect();
    }
  } catch {
    /* non-fatal: the point is best-effort cleanup. */
  }
}
